/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2018. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Injectable } from '@angular/core';
import { PlayerAPI } from 'bitmovin-player/types/core/PlayerAPI';

@Injectable()
export class VideoPlayerService {
  public playbackSpeedOpts: any;
  private nextproUi;
  private speed = 2;

  private readonly FF_SPPED_KEY = 'FF_SPEED_KEY';
  private playerEvents;
  private playbackTimeLabelMode;

  constructor() {
    this.speed = +localStorage.getItem(this.FF_SPPED_KEY) || 2;
  }

  async buildUi(player: PlayerAPI) {
    const {
      BufferingOverlay,
      Container,
      ControlBar,
      ErrorMessageOverlay,
      FullscreenToggleButton,
      HugePlaybackToggleButton,
      PlaybackSpeedSelectBox,
      PlaybackTimeLabel,
      PlaybackTimeLabelMode,
      PlaybackToggleButton,
      PlaybackToggleOverlay,
      RecommendationOverlay,
      SeekBar,
      SeekBarLabel,
      SettingsPanel,
      SettingsPanelItem,
      SettingsPanelPage,
      SettingsToggleButton,
      Spacer,
      ToggleButton,
      UIContainer,
      VideoQualitySelectBox,
      VolumeSlider,
      VolumeToggleButton,
    } = await import('bitmovin-player-ui');

    const { PlayerEvent } = await import('bitmovin-player');
    this.playerEvents = PlayerEvent;
    this.playbackTimeLabelMode = PlaybackTimeLabelMode;

    this.playbackSpeedOpts = new PlaybackSpeedSelectBox({ hidden: true });
    this.playbackSpeedOpts.defaultPlaybackSpeeds = [0.5, 1, 1.5, 2, 4];

    const playbackBtn = new PlaybackToggleButton();
    const volumeToggleButton = new VolumeToggleButton();
    const volumeSlider = new VolumeSlider({ hidden: true });
    const slowAdvance = new ToggleButton({ cssClasses: ['slow-advance'] });
    const slowRewind = new ToggleButton({ disabled: true, cssClasses: ['slow-rewind'] });
    const fastAdvance = new ToggleButton({ cssClasses: ['fast-advance'] });
    const fastRewind = new ToggleButton({ disabled: true, cssClasses: ['fast-rewind'] });

    const mainSettingsPanelPage = new SettingsPanelPage({
      components: [new SettingsPanelItem('Video Quality', new VideoQualitySelectBox())],
    });

    const mobileSettingsPanelPage = new SettingsPanelPage({
      components: [
        new SettingsPanelItem('Video Quality', new VideoQualitySelectBox()),
        new SettingsPanelItem('Playback Speed', this.playbackSpeedOpts, { cssClasses: ['settings-playback-speed'] }),
      ],
      cssClasses: ['settings-btn-mobile'],
    });

    const mobileSettingsPanel = new SettingsPanel({
      components: [mobileSettingsPanelPage],
      hidden: true,
    });

    const settingsPanel = new SettingsPanel({
      components: [mainSettingsPanelPage],
      hidden: true,
    });

    volumeSlider.onHoverChanged.subscribe((el) => {
      if (el.isHovered()) {
        volumeSlider.show();
      } else if (!el.isHovered() && !el.isHidden()) {
        setTimeout(() => (!volumeToggleButton.isHovered() ? volumeSlider.hide() : null), 1000);
      }
    });

    volumeToggleButton.onHoverChanged.subscribe((el) => {
      if (el.isHovered() && volumeSlider.isHidden()) {
        volumeSlider.show();
      } else if (!el.isHovered() && !volumeSlider.isHidden()) {
        setTimeout(() => (!volumeSlider.isHovered() ? volumeSlider.hide() : null), 300);
      }
    });

    const skip5f = new ToggleButton({ cssClasses: ['skip-5f'] });
    skip5f.onClick.subscribe(() => {
      player.seek(player.getCurrentTime() + 5);
    });
    const skip5b = new ToggleButton({ cssClasses: ['skip-5b'] });
    skip5b.onClick.subscribe(() => {
      player.seek(player.getCurrentTime() - 5);
    });

    slowAdvance.onClick.subscribe((btn) => {
      this.setPlaybackSpeedAndPlay(player, 0.5, playbackBtn, slowAdvance);
      fastRewind.off();
      fastAdvance.off();
      slowRewind.off();
    });

    fastAdvance.onClick.subscribe((btn) => {
      this.setPlaybackSpeedAndPlay(player, this.speed, playbackBtn, fastAdvance);
      fastRewind.off();
      slowAdvance.off();
      slowRewind.off();
    });

    playbackBtn.onClick.subscribe((btn: any) => {
      if (btn.isOff() && (slowAdvance.isOn() || slowRewind.isOn() || fastAdvance.isOn() || fastRewind.isOn())) {
        fastAdvance.off();
        fastRewind.off();
        slowAdvance.off();
        slowRewind.off();
        player.setPlaybackSpeed(1);
        setTimeout(() => {
          player.play();
        }, 0);
      }
    });

    player.on(PlayerEvent.Paused, () => {
      fastAdvance.off();
      fastRewind.off();
      slowAdvance.off();
      slowRewind.off();
      player.setPlaybackSpeed(1);
    });

    player.on(PlayerEvent.PlaybackFinished, () => {
      fastAdvance.off();
      fastRewind.off();
      slowAdvance.off();
      slowRewind.off();
    });

    const controlBar = new ControlBar({
      components: [
        settingsPanel,
        new Container({
          components: [
            new PlaybackTimeLabel({ timeLabelMode: PlaybackTimeLabelMode.CurrentTime, hideInLivePlayback: true }),
            new SeekBar({ label: new SeekBarLabel() }),
            new PlaybackTimeLabel({ timeLabelMode: PlaybackTimeLabelMode.TotalTime, cssClasses: ['text-right'] }),
          ],
          cssClasses: ['controlbar-top'],
        }),
        new Container({
          components: [
            new Container({
              components: [slowAdvance, skip5b, playbackBtn, skip5f, fastAdvance],
              cssClasses: ['controlbar-bottom', 'position-center'],
            }),
            new Spacer(),
            volumeToggleButton,
            volumeSlider,
            new SettingsToggleButton({ settingsPanel }),
            new FullscreenToggleButton(),
          ],
          cssClasses: ['controlbar-bottom'],
        }),
      ],
    });

    const container = new UIContainer({
      components: [
        new BufferingOverlay(),
        new PlaybackToggleOverlay(),
        controlBar,
        new RecommendationOverlay({ components: [new HugePlaybackToggleButton()] }),
        new ErrorMessageOverlay(),
        mobileSettingsPanel,
        new SettingsToggleButton({ settingsPanel: mobileSettingsPanel, cssClasses: ['mobile-settings-btn'] }),
      ],
    });
    return container;
  }

  getUi(player: PlayerAPI) {
    return this.buildUi(player);
  }

  private async setPlaybackSpeedAndPlay(player: PlayerAPI, playbackSpeed: number, playbackBtn, button) {
    if (player.getPlaybackSpeed() !== playbackSpeed) {
      player.setPlaybackSpeed(playbackSpeed);
      await player.play();
      setTimeout(() => playbackBtn.off(), 50);
      button.on();
    } else {
      player.setPlaybackSpeed(1);
      if (player.isPlaying()) {
        playbackBtn.on();
      }
      button.off();
    }
  }
}
